import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { HelpIcon } from './HelpIcon';
import { TooltipProvider } from './ui/tooltip';
import { getHelpContent } from '../lib/helpContent';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import { 
  CalendarIcon, 
  ArrowUpIcon, 
  ArrowDownIcon,
  ArrowRightIcon,
  DocumentArrowDownIcon,
  ChartBarIcon,
  EyeIcon,
  CursorArrowRaysIcon,
  HeartIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  XMarkIcon,
  CheckCircleIcon,
  MapPinIcon,
  LinkIcon
} from '@heroicons/react/24/outline';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler
);

interface DashboardKPIs {
  total_sends: number;
  delivery_rate: number;
  open_rate: number;
  click_rate: number;
  happy_rate: number;
  open_feedback_count: number;
  google_places_rating?: number;
  google_places_review_count?: number;
  last_updated: string;
}

interface AnalyticsOverview {
  period: string;
  start_date: string;
  end_date: string;
  total_campaigns: number;
  total_sends: number;
  total_delivered: number;
  total_opens: number;
  total_clicks: number;
  total_feedback: number;
  delivery_rate: number;
  open_rate: number;
  click_rate: number;
  happy_rate: number;
  bounce_rate: number;
  unsubscribe_rate: number;
  trends: Record<string, number>;
}

interface ExportJob {
  id: string;
  export_type: string;
  status: string;
  created_at: string;
  completed_at?: string;
  download_url?: string;
  total_records?: number;
}

interface AnalyticsSnapshot {
  id: string;
  date: string;
  period_type: string;
  total_campaigns: number;
  active_campaigns: number;
  completed_campaigns: number;
  total_sends: number;
  email_sends: number;
  sms_sends: number;
  total_delivered: number;
  email_delivered: number;
  sms_delivered: number;
  delivery_rate: number;
  total_opens: number;
  email_opens: number;
  open_rate: number;
  total_clicks: number;
  click_rate: number;
  unique_clicks: number;
  total_feedback: number;
  positive_feedback: number;
  negative_feedback: number;
  happy_rate: number;
  total_bounces: number;
  bounce_rate: number;
  total_unsubscribes: number;
  unsubscribe_rate: number;
  total_contacts: number;
  active_contacts: number;
  new_contacts: number;
  created_at: string;
  updated_at: string;
}

interface GooglePlace {
  place_id: string;
  name: string;
  formatted_address: string;
  rating?: number;
  user_ratings_total?: number;
  business_status?: string;
  types: string[];
}

interface GooglePlaceSearchResponse {
  places: GooglePlace[];
  total_results: number;
}

interface AdvancedFilters {
  minDeliveryRate?: number;
  minOpenRate?: number;
  minClickRate?: number;
  campaignIds?: string[];
  dateFrom?: string;
  dateTo?: string;
}

const AnalyticsDashboard: React.FC = () => {
  const queryClient = useQueryClient();
  const { user, tenant } = useAuth();
  const [dateRange, setDateRange] = useState('30d');
  const [selectedPeriod, setSelectedPeriod] = useState('daily');
  
  // Google Places integration state
  const [showGooglePlacesModal, setShowGooglePlacesModal] = useState(false);
  const [googlePlaceSearchQuery, setGooglePlaceSearchQuery] = useState('');
  const [googlePlaceAddress, setGooglePlaceAddress] = useState('');
  
  // Advanced filtering state
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<AdvancedFilters>({});
  
  // Error and notification state
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Fetch feature availability for advanced analytics
  const { data: features, isLoading: featuresLoading, error: featuresError } = useQuery({
    queryKey: ['features', tenant?.id], // Include tenant ID to match MainLayout and refetch when tenant changes
    queryFn: async () => {
      const response = await api.billing.getFeatures();
      // Response structure: { data: { plan: string, plan_level: number, features: {...} } }
      const featuresData = response.data?.features || {};
      console.debug('AnalyticsDashboard - Features loaded:', featuresData, 'Full response:', response.data);
      return featuresData;
    },
    enabled: !!user && !!tenant, // Only fetch if user and tenant exist
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    retry: 1,
  });

  // Log feature errors and loading state for debugging
  useEffect(() => {
    if (featuresError) {
      console.error('AnalyticsDashboard - Error loading features:', featuresError);
    }
    if (featuresLoading) {
      console.debug('AnalyticsDashboard - Loading features...');
    }
    if (features) {
      console.debug('AnalyticsDashboard - Features state:', features, 'advanced_analytics:', features.advanced_analytics);
    }
  }, [featuresError, featuresLoading, features]);

  const hasAdvancedAnalytics = features && features.advanced_analytics === true;

  // Auto-clear notifications after 5 seconds
  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError(null);
        setSuccess(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  // Fetch dashboard KPIs
  const { data: kpis, isLoading: kpisLoading, error: kpisError } = useQuery({
    queryKey: ['analytics', 'dashboard', 'kpis'],
    queryFn: async (): Promise<DashboardKPIs> => {
      try {
        const response = await api.analytics.getDashboardKPIs();
        console.log('Analytics Dashboard KPIs response:', response.data);
        return response.data;
      } catch (err: any) {
        console.error('Error fetching analytics dashboard KPIs:', err);
        setError(err?.response?.data?.detail || 'Failed to load dashboard KPIs');
        throw err;
      }
    },
    refetchInterval: 30000, // Refetch every 30 seconds to match DashboardPage
    retry: 2,
  });

  // Fetch analytics overview
  const { data: overview, isLoading: overviewLoading, error: overviewError } = useQuery({
    queryKey: ['analytics', 'overview', dateRange, selectedPeriod, filters],
    queryFn: async (): Promise<AnalyticsOverview> => {
      try {
        const startDate = getStartDate(dateRange);
        const params = {
          period_type: selectedPeriod,
          start_date: startDate?.toISOString(),
          end_date: new Date().toISOString(),
          ...filters,
        };

        const response = await api.analytics.getOverview(params);
        return response.data;
      } catch (err: any) {
        setError(err?.response?.data?.detail || 'Failed to load analytics overview');
        throw err;
      }
    },
    refetchInterval: 30000, // Refetch every 30 seconds to keep data updated
    retry: 2,
  });

  // Fetch export jobs (only if advanced analytics is available)
  const { data: exportJobs, refetch: refetchExportJobs, isLoading: exportsLoading } = useQuery({
    queryKey: ['analytics', 'exports'],
    queryFn: async (): Promise<ExportJob[]> => {
      try {
        const response = await api.analytics.listExports({ limit: 10, offset: 0 });
        return Array.isArray(response.data) ? response.data : [];
      } catch (err: any) {
        console.error('Failed to load exports:', err);
        return [];
      }
    },
    enabled: hasAdvancedAnalytics, // Only fetch if advanced analytics is available
    // Auto-refresh every 3 seconds if there are pending or processing exports
    refetchInterval: (data) => {
      const hasPendingExports = data?.some(job => 
        job.status === 'pending' || job.status === 'processing'
      );
      return hasPendingExports ? 3000 : false; // 3 seconds if pending, otherwise don't poll
    },
  });

  // Fetch current Google Place
  const { data: currentGooglePlace, refetch: refetchGooglePlace } = useQuery({
    queryKey: ['analytics', 'google-place'],
    queryFn: async () => {
      try {
        const response = await api.analytics.getGooglePlace();
        return response.data;
      } catch (err) {
        return null;
      }
    },
  });

  // Fetch snapshots (only if advanced analytics is available)
  const { data: snapshots, refetch: refetchSnapshots, isLoading: snapshotsLoading } = useQuery({
    queryKey: ['analytics', 'snapshots'],
    queryFn: async (): Promise<AnalyticsSnapshot[]> => {
      try {
        const response = await api.analytics.listSnapshots({ period_type: 'daily', limit: 30 });
        // Response structure: { data: { snapshots: [...] } }
        const snapshotsData = response.data?.snapshots || response.data || [];
        return Array.isArray(snapshotsData) ? snapshotsData : [];
      } catch (err: any) {
        console.error('Failed to load snapshots:', err);
        return [];
      }
    },
    enabled: hasAdvancedAnalytics, // Only fetch if advanced analytics is available
    retry: 1,
  });

  // Create snapshot mutation
  const createSnapshotMutation = useMutation({
    mutationFn: async () => {
      const response = await api.analytics.createSnapshot();
      return response.data;
    },
    onSuccess: () => {
      setSuccess('Snapshot created successfully!');
      refetchSnapshots();
    },
    onError: (err: any) => {
      setError(err?.response?.data?.detail || 'Failed to create snapshot');
    },
  });

  // Fetch campaign analytics for sends chart
  const { data: campaignAnalytics } = useQuery({
    queryKey: ['analytics', 'campaigns', dateRange, selectedPeriod, filters],
    queryFn: async () => {
      try {
        const startDate = getStartDate(dateRange);
        const response = await api.analytics.getCampaignAnalytics({
          start_date: startDate?.toISOString(),
          end_date: new Date().toISOString(),
          period_type: selectedPeriod,
        });
        console.log('Campaign analytics response:', response.data);
        return response.data;
      } catch (err) {
        console.error('Failed to load campaign analytics:', err);
        return [];
      }
    },
    retry: 2,
  });

  // Fetch feedback analytics for feedback breakdown chart
  const { data: feedbackAnalytics, error: feedbackError, isLoading: feedbackLoading } = useQuery({
    queryKey: ['analytics', 'feedback', dateRange, selectedPeriod, filters],
    queryFn: async () => {
      try {
        const startDate = getStartDate(dateRange);
        console.log('Fetching feedback analytics with params:', {
          start_date: startDate?.toISOString(),
          end_date: new Date().toISOString(),
        });
        
        const response = await api.analytics.getFeedbackAnalytics({
          start_date: startDate?.toISOString(),
          end_date: new Date().toISOString(),
        });
        
        console.log('Feedback analytics API response:', response);
        return response.data;
      } catch (err) {
        console.error('Failed to load feedback analytics:', err);
        return [];
      }
    },
    retry: 2,
  });

  // Log feedback analytics state
  console.log('Feedback analytics query state:', {
    data: feedbackAnalytics,
    error: feedbackError,
    isLoading: feedbackLoading,
  });

  // Create export mutation
  const createExportMutation = useMutation({
    mutationFn: async (exportType: string) => {
      const exportRequest = {
        export_type: exportType,
        start_date: filters.dateFrom || getStartDate(dateRange)?.toISOString(),
        end_date: filters.dateTo || new Date().toISOString(),
        filters: filters,
        format: 'csv',
      };
      const response = await api.analytics.exportData(exportRequest);
      return response.data;
    },
    onSuccess: () => {
      setSuccess('Export job created successfully! Check the Recent Exports section.');
      refetchExportJobs();
    },
    onError: (err: any) => {
      setError(err?.response?.data?.detail || 'Failed to create export');
    },
  });

  // Download export mutation
  const downloadExportMutation = useMutation({
    mutationFn: async (exportId: string) => {
      // First check if export is ready
      const statusResponse = await api.analytics.getExportStatus(exportId);
      const exportData = statusResponse.data;
      
      if (exportData.status !== 'completed') {
        throw new Error('Export is not ready yet. Please wait for it to complete.');
      }
      
      // Download the file as a blob
      const downloadResponse = await api.analytics.downloadExport(exportId);
      return { blob: downloadResponse.data, exportData };
    },
    onSuccess: ({ blob, exportData }) => {
      // Create a download link and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `export_${exportData.export_type}_${new Date(exportData.created_at).toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      if (exportData.total_records === 0) {
        setSuccess('Export downloaded! Note: The export contains no records (no data for the selected period).');
      } else {
        setSuccess(`Export downloaded! ${exportData.total_records} records exported.`);
      }
    },
    onError: (err: any) => {
      setError(err?.message || err?.response?.data?.detail || 'Failed to download export');
    },
  });

  // Search Google Places mutation
  const searchGooglePlacesMutation = useMutation({
    mutationFn: async () => {
      const response = await api.analytics.searchGooglePlace({
        query: googlePlaceSearchQuery,
        location: googlePlaceAddress,
      });
      return response.data as GooglePlaceSearchResponse;
    },
    onError: (err: any) => {
      setError(err?.response?.data?.detail || 'Failed to search Google Places');
    },
  });

  // Link Google Place mutation
  const linkGooglePlaceMutation = useMutation({
    mutationFn: async (placeId: string) => {
      const response = await api.analytics.linkGooglePlace(placeId);
      return response.data;
    },
    onSuccess: () => {
      setSuccess('Google Place linked successfully!');
      setShowGooglePlacesModal(false);
      refetchGooglePlace();
      setGooglePlaceSearchQuery('');
      setGooglePlaceAddress('');
      // Invalidate rating settings to ensure sync
      queryClient.invalidateQueries({ queryKey: ['rating-settings'] });
    },
    onError: (err: any) => {
      setError(err?.response?.data?.detail || 'Failed to link Google Place');
    },
  });

  function getStartDate(range: string): Date | null {
    const now = new Date();
    switch (range) {
      case '7d':
        return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      case '30d':
        return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      case '90d':
        return new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
      default:
        return null;
    }
  }

  function formatNumber(num: number): string {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  }

  function formatPercentage(num: number): string {
    return num.toFixed(1) + '%';
  }

  function getTrendIcon(change: number) {
    if (change > 0) return <ArrowUpIcon className="h-4 w-4 text-green-500" />;
    if (change < 0) return <ArrowDownIcon className="h-4 w-4 text-red-500" />;
    return <ArrowRightIcon className="h-4 w-4 text-gray-500" />;
  }

  function getTrendColor(change: number): string {
    if (change > 0) return 'text-green-600';
    if (change < 0) return 'text-red-600';
    return 'text-gray-600';
  }

  // Chart data for sends over time
  const sendsChartData = {
    labels: campaignAnalytics && Array.isArray(campaignAnalytics) && campaignAnalytics.length > 0 
      ? campaignAnalytics.map((item: any) => {
          if (!item || !item.date) return 'Invalid Date';
          try {
            const date = new Date(item.date);
            return isNaN(date.getTime()) ? 'Invalid Date' : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          } catch (e) {
            return 'Invalid Date';
          }
        })
      : ['No Data'],
    datasets: [
      {
        label: 'Total Sends',
        data: campaignAnalytics && Array.isArray(campaignAnalytics) && campaignAnalytics.length > 0 
          ? campaignAnalytics.map((item: any) => {
              // Handle both sent_count and total_sends properties
              return item?.sent_count ?? item?.total_sends ?? 0;
            })
          : [0],
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        tension: 0.4,
        fill: true,
      },
      {
        label: 'Delivered',
        data: campaignAnalytics && Array.isArray(campaignAnalytics) && campaignAnalytics.length > 0 
          ? campaignAnalytics.map((item: any) => item?.delivered_count ?? 0)
          : [0],
        borderColor: 'rgb(16, 185, 129)',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        tension: 0.4,
        fill: false,
      },
    ],
  };

  // Chart data for engagement rates - use KPI data (real-time) instead of overview (may have zeros)
  const engagementChartData = {
    labels: ['Delivery Rate', 'Open Rate', 'Click Rate', 'Happy Rate'],
    datasets: [
      {
        label: 'Rate (%)',
        data: [
          Number(kpis?.delivery_rate || overview?.delivery_rate) || 0,
          Number(kpis?.open_rate || overview?.open_rate) || 0,
          Number(kpis?.click_rate || overview?.click_rate) || 0,
          Number(kpis?.happy_rate || overview?.happy_rate) || 0,
        ],
        backgroundColor: [
          'rgba(59, 130, 246, 0.8)',
          'rgba(16, 185, 129, 0.8)',
          'rgba(245, 158, 11, 0.8)',
          'rgba(236, 72, 153, 0.8)',
        ],
        borderColor: [
          'rgb(59, 130, 246)',
          'rgb(16, 185, 129)',
          'rgb(245, 158, 11)',
          'rgb(236, 72, 153)',
        ],
        borderWidth: 2,
      },
    ],
  };

  // Chart data for feedback breakdown
  const feedbackChartData = (() => {
    // Aggregate feedback analytics data
    let positiveCount = 0;
    let neutralCount = 0;
    let negativeCount = 0;
    
    console.log('Feedback Analytics Data:', feedbackAnalytics);
    
    // Handle both array and single object responses
    if (feedbackAnalytics) {
      const dataArray = Array.isArray(feedbackAnalytics) ? feedbackAnalytics : [feedbackAnalytics];
      
      if (dataArray.length > 0) {
        dataArray.forEach((item: any) => {
          console.log('Processing feedback item:', item);
          // Check if item has the expected structure
          if (item && typeof item === 'object') {
            positiveCount += (item.five_star_count || 0) + (item.four_star_count || 0);
            neutralCount += item.three_star_count || 0;
            negativeCount += (item.two_star_count || 0) + (item.one_star_count || 0);
          }
        });
      }
    }
    
    const total = positiveCount + neutralCount + negativeCount;
    console.log('Feedback Breakdown Totals:', { positiveCount, neutralCount, negativeCount, total });
    
    const chartData = {
      labels: ['Positive (4-5 stars)', 'Neutral (3 stars)', 'Negative (1-2 stars)'],
      datasets: [
        {
          data: [positiveCount, neutralCount, negativeCount],
          backgroundColor: [
            'rgba(16, 185, 129, 0.8)',
            'rgba(245, 158, 11, 0.8)',
            'rgba(239, 68, 68, 0.8)',
          ],
          borderColor: [
            'rgb(16, 185, 129)',
            'rgb(245, 158, 11)',
            'rgb(239, 68, 68)',
          ],
          borderWidth: 2,
        },
      ],
    };
    
    console.log('Final chart data:', chartData);
    return chartData;
  })();

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
      },
    },
  };

  // Loading state
  if (kpisLoading || overviewLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading analytics...</p>
          </div>
        </div>
      </div>
    );
  }

  const analyticsHelp = getHelpContent('analyticsDashboard');

  return (
    <TooltipProvider>
      <div className="space-y-6">
        {/* Notification Banners */}
        {error && (
          <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mr-3" />
              <div>
                <p className="text-sm text-red-800">{error}</p>
              </div>
              <button
                onClick={() => setError(null)}
                className="ml-auto text-red-400 hover:text-red-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}

        {success && (
          <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded-md">
            <div className="flex items-center">
              <CheckCircleIcon className="h-5 w-5 text-green-400 mr-3" />
              <div>
                <p className="text-sm text-green-800">{success}</p>
              </div>
              <button
                onClick={() => setSuccess(null)}
                className="ml-auto text-green-400 hover:text-green-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h1>
              {analyticsHelp && (
                <HelpIcon
                  tooltip="Learn about analytics metrics"
                  modalTitle={analyticsHelp.title}
                  modalContent={analyticsHelp.content}
                  size="md"
                />
              )}
            </div>
            <p className="text-gray-600">Monitor your reputation management performance</p>
          </div>
        
        <div className="flex space-x-3">
          {/* Google Places Integration Button */}
          <button
            onClick={() => setShowGooglePlacesModal(true)}
            className="flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <MapPinIcon className="h-4 w-4 mr-2" />
            {currentGooglePlace?.linked ? 'Google Place Linked' : 'Link Google Place'}
          </button>

          {/* Advanced Filters Button - Only show if advanced analytics is available */}
          {hasAdvancedAnalytics && (
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <FunnelIcon className="h-4 w-4 mr-2" />
              Filters
              {Object.keys(filters).length > 0 && (
                <span className="ml-2 bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded-full">
                  {Object.keys(filters).length}
                </span>
              )}
            </button>
          )}

          {/* Date Range Selector */}
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
          </select>
          
          {/* Period Selector */}
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
        </div>
      </div>

      {/* Advanced Filters Panel - Only show if advanced analytics is available */}
      {hasAdvancedAnalytics && showFilters && (
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Advanced Filters</h3>
            <button
              onClick={() => {
                setFilters({});
                setShowFilters(false);
              }}
              className="text-sm text-gray-600 hover:text-gray-800"
            >
              Clear All
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Min Delivery Rate (%)
              </label>
              <input
                type="number"
                min="0"
                max="100"
                value={filters.minDeliveryRate || ''}
                onChange={(e) => setFilters({ ...filters, minDeliveryRate: parseFloat(e.target.value) || undefined })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., 90"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Min Open Rate (%)
              </label>
              <input
                type="number"
                min="0"
                max="100"
                value={filters.minOpenRate || ''}
                onChange={(e) => setFilters({ ...filters, minOpenRate: parseFloat(e.target.value) || undefined })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., 20"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Min Click Rate (%)
              </label>
              <input
                type="number"
                min="0"
                max="100"
                value={filters.minClickRate || ''}
                onChange={(e) => setFilters({ ...filters, minClickRate: parseFloat(e.target.value) || undefined })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., 5"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date From
              </label>
              <input
                type="date"
                value={filters.dateFrom || ''}
                onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value || undefined })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date To
              </label>
              <input
                type="date"
                value={filters.dateTo || ''}
                onChange={(e) => setFilters({ ...filters, dateTo: e.target.value || undefined })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="mt-4 flex justify-end">
            <button
              onClick={() => setShowFilters(false)}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Apply Filters
            </button>
          </div>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Sends */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Sends</p>
              <p className="text-2xl font-bold text-gray-900">{formatNumber(kpis?.total_sends || 0)}</p>
            </div>
            <ChartBarIcon className="h-8 w-8 text-blue-600" />
          </div>
          <div className="mt-2 flex items-center text-sm">
            {getTrendIcon(overview?.trends.total_sends_change || 0)}
            <span className={`ml-1 ${getTrendColor(overview?.trends.total_sends_change || 0)}`}>
              {Math.abs(overview?.trends.total_sends_change || 0).toFixed(1)}%
            </span>
            <span className="ml-1 text-gray-500">vs last period</span>
          </div>
        </div>

        {/* Delivery Rate */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Delivery Rate</p>
              <p className="text-2xl font-bold text-gray-900">{formatPercentage(kpis?.delivery_rate || 0)}</p>
            </div>
            <ArrowUpIcon className="h-8 w-8 text-green-600" />
          </div>
          <div className="mt-2 flex items-center text-sm">
            {getTrendIcon(overview?.trends.delivery_rate_change || 0)}
            <span className={`ml-1 ${getTrendColor(overview?.trends.delivery_rate_change || 0)}`}>
              {Math.abs(overview?.trends.delivery_rate_change || 0).toFixed(1)}%
            </span>
            <span className="ml-1 text-gray-500">vs last period</span>
          </div>
        </div>

        {/* Open Rate */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Open Rate</p>
              <p className="text-2xl font-bold text-gray-900">{formatPercentage(kpis?.open_rate || 0)}</p>
            </div>
            <EyeIcon className="h-8 w-8 text-purple-600" />
          </div>
          <div className="mt-2 flex items-center text-sm">
            {getTrendIcon(overview?.trends.open_rate_change || 0)}
            <span className={`ml-1 ${getTrendColor(overview?.trends.open_rate_change || 0)}`}>
              {Math.abs(overview?.trends.open_rate_change || 0).toFixed(1)}%
            </span>
            <span className="ml-1 text-gray-500">vs last period</span>
          </div>
        </div>

        {/* Click Rate */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Click Rate</p>
              <p className="text-2xl font-bold text-gray-900">{formatPercentage(kpis?.click_rate || 0)}</p>
            </div>
            <CursorArrowRaysIcon className="h-8 w-8 text-orange-600" />
          </div>
          <div className="mt-2 flex items-center text-sm">
            {getTrendIcon(overview?.trends.click_rate_change || 0)}
            <span className={`ml-1 ${getTrendColor(overview?.trends.click_rate_change || 0)}`}>
              {Math.abs(overview?.trends.click_rate_change || 0).toFixed(1)}%
            </span>
            <span className="ml-1 text-gray-500">vs last period</span>
          </div>
        </div>

        {/* Happy Rate */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Happy Rate</p>
              <p className="text-2xl font-bold text-gray-900">{formatPercentage(kpis?.happy_rate || 0)}</p>
            </div>
            <HeartIcon className="h-8 w-8 text-pink-600" />
          </div>
          <div className="mt-2 flex items-center text-sm">
            {getTrendIcon(overview?.trends.happy_rate_change || 0)}
            <span className={`ml-1 ${getTrendColor(overview?.trends.happy_rate_change || 0)}`}>
              {Math.abs(overview?.trends.happy_rate_change || 0).toFixed(1)}%
            </span>
            <span className="ml-1 text-gray-500">vs last period</span>
          </div>
        </div>

        {/* Open Feedback */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Open Feedback</p>
              <p className="text-2xl font-bold text-gray-900">{kpis?.open_feedback_count || 0}</p>
            </div>
            <ExclamationTriangleIcon className="h-8 w-8 text-yellow-600" />
          </div>
          <div className="mt-2 text-sm text-gray-500">
            Requires attention
          </div>
        </div>

        {/* Google Places Rating */}
        {kpis?.google_places_rating && (
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Google Rating</p>
                <p className="text-2xl font-bold text-gray-900">{kpis.google_places_rating.toFixed(1)}</p>
                <p className="text-sm text-gray-500">{kpis.google_places_review_count} reviews</p>
              </div>
              <div className="text-3xl">⭐</div>
            </div>
          </div>
        )}
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sends Over Time */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Sends Over Time</h3>
          <div className="h-64">
            <Line data={sendsChartData} options={chartOptions} />
          </div>
        </div>

        {/* Engagement Rates */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Engagement Rates</h3>
          <div className="h-64">
            <Bar data={engagementChartData} options={chartOptions} />
          </div>
        </div>

        {/* Feedback Breakdown */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Feedback Breakdown</h3>
          <div className="h-64">
            <Doughnut data={feedbackChartData} options={doughnutOptions} />
          </div>
        </div>

        {/* Export Section - Only show if advanced analytics is available */}
        {hasAdvancedAnalytics ? (
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
              Data Export
            </h3>
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Export your analytics data for further analysis. Applies current filters.
              </p>
            
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => createExportMutation.mutate('campaigns')}
                disabled={createExportMutation.isPending}
                className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {createExportMutation.isPending ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-700 mr-2"></div>
                ) : (
                  <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                )}
                Campaigns
              </button>
              
              <button
                onClick={() => createExportMutation.mutate('feedback')}
                disabled={createExportMutation.isPending}
                className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {createExportMutation.isPending ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-700 mr-2"></div>
                ) : (
                  <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                )}
                Feedback
              </button>
              
              <button
                onClick={() => createExportMutation.mutate('analytics')}
                disabled={createExportMutation.isPending}
                className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {createExportMutation.isPending ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-700 mr-2"></div>
                ) : (
                  <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                )}
                Analytics
              </button>
              
              <button
                onClick={() => createExportMutation.mutate('clicks')}
                disabled={createExportMutation.isPending}
                className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {createExportMutation.isPending ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-700 mr-2"></div>
                ) : (
                  <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                )}
                Clicks
              </button>
            </div>

            {/* Recent Exports */}
            {exportsLoading ? (
              <div className="mt-6 text-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-sm text-gray-600 mt-2">Loading exports...</p>
              </div>
            ) : exportJobs && exportJobs.length > 0 ? (
              <div className="mt-6">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium text-gray-900">Recent Exports</h4>
                  {exportJobs.some(job => job.status === 'pending' || job.status === 'processing') && (
                    <span className="text-xs text-blue-600 flex items-center">
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600 mr-1"></div>
                      Auto-updating...
                    </span>
                  )}
                </div>
                <div className="space-y-2">
                  {exportJobs.slice(0, 5).map((job) => (
                    <div key={job.id} className="flex items-center justify-between text-sm p-2 hover:bg-gray-50 rounded">
                      <div className="flex-1">
                        <div className="flex items-center">
                          <span className="font-medium capitalize">{job.export_type}</span>
                          {job.total_records !== undefined && job.total_records !== null && (
                            <span className={`ml-2 text-xs ${job.total_records === 0 ? 'text-yellow-600' : 'text-gray-500'}`}>
                              ({job.total_records} record{job.total_records !== 1 ? 's' : ''})
                            </span>
                          )}
                        </div>
                        <span className="text-gray-500 text-xs">
                          {new Date(job.created_at).toLocaleString()}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          job.status === 'completed' ? 'bg-green-100 text-green-800' :
                          job.status === 'processing' ? 'bg-yellow-100 text-yellow-800 animate-pulse' :
                          job.status === 'failed' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {job.status}
                        </span>
                        {job.status === 'completed' && (
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              downloadExportMutation.mutate(job.id);
                            }}
                            disabled={downloadExportMutation.isPending}
                            className="text-blue-600 hover:text-blue-800 font-medium disabled:opacity-50"
                          >
                            {downloadExportMutation.isPending ? 'Downloading...' : 'Download'}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="mt-6 text-center py-4 text-gray-500 text-sm">
                No exports yet. Create your first export above!
              </div>
            )}
          </div>
        </div>
        ) : (
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
              Data Export
            </h3>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
              <p className="text-gray-700 mb-4">
                <strong>Advanced Analytics</strong> is available on <strong>Growth+</strong> plans.
              </p>
              <p className="text-sm text-gray-600 mb-4">
                Upgrade to access data exports, snapshots, and advanced filtering options.
              </p>
              <a
                href="/billing"
                className="inline-block px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Upgrade to Growth Plan
              </a>
            </div>
          </div>
        )}
      </div>

      {/* Snapshots Section - Only show if advanced analytics is available */}
      {hasAdvancedAnalytics && (
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <ChartBarIcon className="h-5 w-5 mr-2" />
              Analytics Snapshots
            </h3>
            <button
              onClick={() => createSnapshotMutation.mutate()}
              disabled={createSnapshotMutation.isPending}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {createSnapshotMutation.isPending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Creating...
                </>
              ) : (
                <>
                  <CheckCircleIcon className="h-4 w-4 mr-2" />
                  Create Snapshot
                </>
              )}
            </button>
          </div>
          
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Snapshots are pre-calculated metrics stored for fast historical analysis. 
              They're created automatically each night, but you can create one manually anytime.
            </p>
            
            {/* Recent Snapshots */}
            {snapshotsLoading ? (
              <div className="mt-6 text-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-sm text-gray-600 mt-2">Loading snapshots...</p>
              </div>
            ) : snapshots && snapshots.length > 0 ? (
              <div className="mt-6">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Recent Snapshots</h4>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {snapshots.map((snapshot) => (
                    <div key={snapshot.id} className="flex items-center justify-between text-sm p-3 hover:bg-gray-50 rounded border">
                      <div className="flex-1">
                        <div className="flex items-center">
                          <span className="font-medium">
                            {new Date(snapshot.date).toLocaleDateString('en-US', { 
                              weekday: 'short', 
                              year: 'numeric', 
                              month: 'short', 
                              day: 'numeric' 
                            })}
                          </span>
                          <span className="ml-2 text-xs text-gray-500 capitalize">
                            ({snapshot.period_type})
                          </span>
                        </div>
                        <div className="mt-1 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-gray-600">
                          <div>
                            <span className="font-medium">Sends:</span> {snapshot.total_sends.toLocaleString()}
                          </div>
                          <div>
                            <span className="font-medium">Delivery:</span> {snapshot.delivery_rate.toFixed(1)}%
                          </div>
                          <div>
                            <span className="font-medium">Open:</span> {snapshot.open_rate.toFixed(1)}%
                          </div>
                          <div>
                            <span className="font-medium">Click:</span> {snapshot.click_rate.toFixed(1)}%
                          </div>
                        </div>
                        <div className="mt-1 text-xs text-gray-500">
                          Created: {new Date(snapshot.created_at).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="mt-6 text-center py-4 text-gray-500 text-sm">
                No snapshots yet. Snapshots are created automatically each night, or you can create one manually above.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Google Places Modal */}
      {showGooglePlacesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900 flex items-center">
                <MapPinIcon className="h-6 w-6 mr-2" />
                Link Google Place
              </h2>
              <button
                onClick={() => setShowGooglePlacesModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {currentGooglePlace?.linked ? (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-md">
                <div className="flex items-center">
                  <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2" />
                  <div>
                    <p className="font-medium text-green-900">Google Place Already Linked</p>
                    <p className="text-sm text-green-700 mt-1">
                      {currentGooglePlace.place_data?.place_name || 'Your business'}
                    </p>
                  </div>
                </div>
              </div>
            ) : null}

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Business Name *
                </label>
                <input
                  type="text"
                  value={googlePlaceSearchQuery}
                  onChange={(e) => setGooglePlaceSearchQuery(e.target.value)}
                  placeholder="e.g., Joe's Pizza or ChIJR9nhhVl204kRcZeBm8iyMuU"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  💡 Tip: Include your full address for more accurate results, or paste your Google Place ID
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  🔍 Using Enhanced Multi-Strategy Search with Find Place + Text Search APIs
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  💡 Finds businesses without physical addresses (like real estate consultants)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Address (Recommended for accuracy)
                </label>
                <input
                  type="text"
                  value={googlePlaceAddress}
                  onChange={(e) => setGooglePlaceAddress(e.target.value)}
                  placeholder="123 Main St, San Francisco, CA 94102"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Include full address with ZIP code for best results
                </p>
              </div>

              <button
                onClick={() => searchGooglePlacesMutation.mutate()}
                disabled={!googlePlaceSearchQuery || searchGooglePlacesMutation.isPending}
                className="w-full flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {searchGooglePlacesMutation.isPending ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Searching...
                  </>
                ) : (
                  <>
                    <MagnifyingGlassIcon className="h-4 w-4 mr-2" />
                    Search Google Places
                  </>
                )}
              </button>
            </div>

            {/* Search Results */}
            {searchGooglePlacesMutation.data && (
              <div className="space-y-3">
                {searchGooglePlacesMutation.data.places.length > 0 ? (
                  <>
                    <div className="text-sm text-gray-600 font-medium">
                      Found {searchGooglePlacesMutation.data.places.length} result{searchGooglePlacesMutation.data.places.length !== 1 ? 's' : ''} - Select your business:
                    </div>
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                    {searchGooglePlacesMutation.data.places.map((place: GooglePlace) => (
                      <div
                        key={place.place_id}
                        className="p-4 border border-gray-200 rounded-md hover:border-blue-500 transition-colors"
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900">{place.name}</h4>
                            <p className="text-sm text-gray-600 mt-1">{place.formatted_address}</p>
                            {place.rating && (
                              <div className="flex items-center mt-2 text-sm">
                                <span className="text-yellow-500 mr-1">⭐</span>
                                <span className="font-medium">{place.rating.toFixed(1)}</span>
                                {place.user_ratings_total && (
                                  <span className="text-gray-500 ml-1">
                                    ({place.user_ratings_total} reviews)
                                  </span>
                                )}
                              </div>
                            )}
                            {place.business_status && (
                              <span className={`inline-block mt-2 px-2 py-1 text-xs rounded-full ${
                                place.business_status === 'OPERATIONAL' 
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}>
                                {place.business_status}
                              </span>
                            )}
                          </div>
                          <button
                            onClick={() => linkGooglePlaceMutation.mutate(place.place_id)}
                            disabled={linkGooglePlaceMutation.isPending}
                            className="ml-4 flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 text-sm"
                          >
                            {linkGooglePlaceMutation.isPending ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            ) : (
                              <>
                                <LinkIcon className="h-4 w-4 mr-1" />
                                Link
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    ))}
                    </div>
                  </>
                ) : (
                  <div className="text-sm text-gray-500 py-4 text-center">
                    No results found. Try searching with a different business name or address.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Last Updated */}
      {kpis?.last_updated && (
        <div className="text-center text-sm text-gray-500">
          Last updated: {new Date(kpis.last_updated).toLocaleString()}
        </div>
      )}
      </div>
    </TooltipProvider>
  );
};

export default AnalyticsDashboard;
